import t from 'react-translate';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import {
  ChangeEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isRtl } from 'styles/global_defaults/media-queries';
import { getStyles } from './styles';
import { ScaleLimits } from './types';
import {
  getBottomSliderMark,
  getRangeValue,
  getTicksAndFactor,
  getTopSliderMark,
} from './utils';
import NvTextInput from '../inputs/nv-text-input';
import LimitSettingInput from './limit-setting-input';
import NvTooltip from '../nv-tooltip';

type ValueRange = {
  top: number;
  bottom: number;
};

export type LikertScaleProps = {
  value?: number;
  limits: ScaleLimits;
  setLimits?: (limits: SetStateAction<ScaleLimits>) => void;
  valueRange?: ValueRange;
  isEditing?: boolean;
  isDisabled?: boolean;
  handleColor?: string;
  showHandleColor?: boolean;
  onSelectValue?: (value: number | number[]) => void;
  onLabelChange?: (
    e: ChangeEvent<HTMLInputElement>,
    isLeftLabel: boolean
  ) => void;
  onBlur?: (e, setInputMode, setIsFocused, isMinLimitInput) => void;
};

const LikertScale = ({
  value,
  limits,
  setLimits,
  valueRange,
  isEditing,
  isDisabled,
  handleColor,
  showHandleColor,
  onSelectValue,
  onLabelChange,
  onBlur,
}: LikertScaleProps) => {
  const [marks, setMarks] = useState(null);
  const sliderRef = useRef(null);

  let maxValue = null;
  let minValue = null;

  if (valueRange?.top >= valueRange?.bottom) {
    maxValue = valueRange?.top;
    minValue = valueRange?.bottom;
  } else {
    minValue = valueRange?.top;
    maxValue = valueRange?.bottom;
  }

  if (sliderRef.current) {
    const handle1 = sliderRef.current.querySelector('.rc-slider-handle-1');
    const handle2 = sliderRef.current.querySelector('.rc-slider-handle-2');

    handle1?.setAttribute(
      'data-content',
      getBottomSliderMark(limits, minValue),
    );
    handle2?.setAttribute('data-content', getTopSliderMark(limits, maxValue));
  }

  // To set the marks on likert scale when both left and right limits are set
  useEffect(() => {
    if (limits?.min.value !== null && limits?.max.value !== null) {
      const newMarks = {};
      getTicksAndFactor(limits?.min.value, limits?.max.value).ticksArray.forEach(
        (eachTick) => {
          newMarks[eachTick] = eachTick;
        },
      );
      setMarks(newMarks);
    } else {
      setMarks(null);
    }
  }, [limits?.min.value, limits?.max.value]);

  const areLimitsValid = () => limits?.min.value !== null && limits?.max.value !== null;

  const { factor } = getTicksAndFactor(limits?.min.value, limits?.max.value);

  // handle wrapper function is to wrap the handle element with NvTooltip to show tooltips on each handle
  const handleWrapper = (
    origin: React.ReactElement<React.HTMLAttributes<HTMLDivElement>>,
  ) => {
    // handle1 is for the bottom range value
    const isHandle1 = origin.props.className.includes('rc-slider-handle-1');

    // handle2 is for the upper range value
    const isHandle2 = origin.props.className.includes('rc-slider-handle-2');

    const getText = () => {
      if (valueRange?.top === valueRange?.bottom) {
        return t.QUIZZES.NUMBER_ANSWER_QUESTION.CORRECT_NUMBER();
      }

      if (isHandle1) {
        return t.QUIZZES.LIKERT_SCALE_QUESTION.CORRECT_NUMBER_FROM();
      }
      if (isHandle2) {
        return t.QUIZZES.LIKERT_SCALE_QUESTION.CORRECT_NUMBER_TO();
      }
      return null;
    };
    return (
      <NvTooltip offset={30} text={getText()}>
        {origin}
      </NvTooltip>
    );
  };
  const styles = getStyles({
    value,
    isDisabled,
    limits,
    factor,
    showHandleColor,
    handleColor,
  });

  if (isEditing) {
    return (
      <div css={styles}>
        <div className='slider-container'>
          <NvTooltip text={t.QUIZZES.LIKERT_SCALE_QUESTION.LEFT_SCALE_LIMIT()}>
            <div className='left-limit'>
              <LimitSettingInput
                setLimits={setLimits}
                limits={limits}
                isMinLimitInput
                isFocused={limits?.min.isInputFocused}
                addDataQa={limits?.min.addValueDataQa}
                editDataQa={limits?.min.editValueDataQa}
                onBlur={onBlur}
              />
            </div>
          </NvTooltip>
          <NvTooltip text={t.QUIZZES.LIKERT_SCALE_QUESTION.RIGHT_SCALE_LIMIT()}>
            <div className='right-limit'>
              <LimitSettingInput
                setLimits={setLimits}
                limits={limits}
                isFocused={limits?.max.isInputFocused}
                addDataQa={limits?.max.addValueDataQa}
                editDataQa={limits?.max.editValueDataQa}
                onBlur={onBlur}
              />
            </div>
          </NvTooltip>
          <div ref={sliderRef} className='slide'>
            <Slider
              range
              reverse={isRtl()}
              step={0.1}
              /**
               *If no limits are set, the marks at the extreme ends of the Likert scale will not be displayed as per design.
               *Therefore, dummy values (0 and 1) are provided when both limits are not set.
               */
              min={areLimitsValid() ? limits?.min.value : 0}
              max={areLimitsValid() ? limits?.max.value : 1}
              marks={marks ?? { 0: 0, 1: 1 }}
              handleRender={(origin) => handleWrapper(origin)}
              value={getRangeValue(minValue, maxValue, limits)}
            />
          </div>
        </div>
        <div className='label-section d-flex'>
          <NvTextInput
            placeholder={t.QUIZZES.LIKERT_SCALE_QUESTION.LEFT_LABEL_PLACEHOLDER()}
            className='left-label text-large-body'
            value={limits?.min.label}
            onChange={(e) => onLabelChange(e, true)}
            maxLength={30}
            data-qa={limits?.min.labelDataQa}
          />
          <NvTextInput
            placeholder={t.QUIZZES.LIKERT_SCALE_QUESTION.RIGHT_LABEL_PLACEHOLDER()}
            className='right-label text-large-body'
            value={limits?.max.label}
            onChange={(e) => onLabelChange(e, false)}
            maxLength={30}
            data-qa={limits?.max.labelDataQa}
          />
        </div>
      </div>
    );
  }

  return (
    <div css={styles}>
      <div className='answer-slider'>
        <div ref={sliderRef} className='slide mb-4 px-4'>
          <Slider
            reverse={isRtl()}
            step={1}
            min={limits?.min.value}
            max={limits?.max.value}
            marks={marks}
            value={value}
            onChange={onSelectValue}
            onChangeComplete={onSelectValue}
            disabled={isDisabled}
          />
        </div>
      </div>
      <div className='d-flex bold'>
        <div>{limits?.min.label}</div>
        <div className='ml-auto'>{limits?.max.label}</div>
      </div>
    </div>
  );
};

export default LikertScale;
